import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for managing idle session timeout
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Timeout duration in milliseconds (default: 15 minutes)
 * @param {number} options.warningTime - Warning time before timeout in milliseconds (default: 2 minutes)
 * @param {Function} options.onTimeout - Callback when timeout occurs
 * @param {Function} options.onWarning - Callback when warning period starts
 * @param {Function} options.onActivity - Callback when user activity is detected
 * @param {boolean} options.enabled - Whether the timeout is enabled (default: true)
 * @param {Array} options.events - Events to listen for user activity (default: mouse, keyboard, touch)
 * @returns {Object} - Idle timeout state and controls
 */
export const useIdleTimeout = ({
    timeout = 15 * 60 * 1000, // 15 minutes default
    warningTime = 2 * 60 * 1000, // 2 minutes warning default
    onTimeout = () => {},
    onWarning = () => {},
    onActivity = () => {},
    enabled = true,
    events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
} = {}) => {
    const [isIdle, setIsIdle] = useState(false)
    const [isWarning, setIsWarning] = useState(false)
    const [remainingTime, setRemainingTime] = useState(timeout)

    const intervalRef = useRef(null)
    const startTimeRef = useRef(Date.now())
    const warningTriggeredRef = useRef(false)

    // Store callbacks in refs to avoid dependency issues
    const onTimeoutRef = useRef(onTimeout)
    const onWarningRef = useRef(onWarning)
    const onActivityRef = useRef(onActivity)

    // Update refs when callbacks change
    useEffect(() => {
        onTimeoutRef.current = onTimeout
        onWarningRef.current = onWarning
        onActivityRef.current = onActivity
    }, [onTimeout, onWarning, onActivity])

    // Simple countdown that starts immediately
    useEffect(() => {
        if (!enabled) return

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        // Reset state
        setIsIdle(false)
        setIsWarning(false)
        setRemainingTime(timeout)
        startTimeRef.current = Date.now()
        warningTriggeredRef.current = false

        // Start countdown
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current
            const remaining = Math.max(0, timeout - elapsed)

            setRemainingTime(remaining)

            // Check for warning
            if (remaining <= warningTime && !warningTriggeredRef.current) {
                warningTriggeredRef.current = true
                setIsWarning(true)
                onWarningRef.current()
            }

            // Check for timeout
            if (remaining <= 0) {
                clearInterval(intervalRef.current)
                setIsIdle(true)
                setIsWarning(false)
                onTimeoutRef.current()
            }
        }, 1000)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [enabled, timeout, warningTime])

    // Handle user activity
    const handleActivity = useCallback(() => {
        if (!enabled) return

        // Log activity for debugging, very spammy, uncomment when needed
        // eslint-disable-next-line no-console
        //console.debug('[Idle Timeout] User activity detected')
        onActivityRef.current()

        // Reset timer only if not in warning mode and not already idle
        setIsIdle(prevIsIdle => {
            if (!prevIsIdle) {
                setIsWarning(prevIsWarning => {
                    // Don't reset timer if we're in warning mode - user should use the dialog buttons
                    if (!prevIsWarning) {
                        startTimeRef.current = Date.now()
                        setRemainingTime(timeout)
                        warningTriggeredRef.current = false
                    }
                    return prevIsWarning
                })
            }
            return prevIsIdle
        })
    }, [enabled, timeout])

    // Set up event listeners
    useEffect(() => {
        if (!enabled) return

        events.forEach(event => {
            document.addEventListener(event, handleActivity, true)
        })

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true)
            })
        }
    }, [enabled, events, handleActivity])

    // Manually trigger timeout
    const triggerTimeout = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }
        setIsIdle(true)
        setIsWarning(false)
        onTimeoutRef.current()
    }, [])

    // Manually trigger warning
    const triggerWarning = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        setIsWarning(true)
        setRemainingTime(warningTime)
        warningTriggeredRef.current = true
        onWarningRef.current()

        // Start warning countdown
        const warningStart = Date.now()
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - warningStart
            const remaining = Math.max(0, warningTime - elapsed)

            setRemainingTime(remaining)

            if (remaining <= 0) {
                clearInterval(intervalRef.current)
                setIsIdle(true)
                setIsWarning(false)
                onTimeoutRef.current()
            }
        }, 1000)
    }, [warningTime])

    // Extend session
    const extendSession = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        // Reset everything
        setIsIdle(false)
        setIsWarning(false)
        setRemainingTime(timeout)
        startTimeRef.current = Date.now()
        warningTriggeredRef.current = false

        // Restart countdown
        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current
            const remaining = Math.max(0, timeout - elapsed)

            setRemainingTime(remaining)

            if (remaining <= warningTime && !warningTriggeredRef.current) {
                warningTriggeredRef.current = true
                setIsWarning(true)
                onWarningRef.current()
            }

            if (remaining <= 0) {
                clearInterval(intervalRef.current)
                setIsIdle(true)
                setIsWarning(false)
                onTimeoutRef.current()
            }
        }, 1000)
    }, [timeout, warningTime])

    return {
        isIdle,
        isWarning,
        remainingTime,
        lastActivity: startTimeRef.current,
        resetTimer: extendSession,
        triggerTimeout,
        triggerWarning,
        extendSession,
        pauseTimer: () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        },
        resumeTimer: extendSession,
        // Utility functions
        getRemainingTimeFormatted: () => {
            const minutes = Math.floor(remainingTime / 60000)
            const seconds = Math.floor((remainingTime % 60000) / 1000)
            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        },
        getWarningTimeFormatted: () => {
            const warningRemaining = Math.max(0, remainingTime)
            const minutes = Math.floor(warningRemaining / 60000)
            const seconds = Math.floor((warningRemaining % 60000) / 1000)
            return `${minutes}:${seconds.toString().padStart(2, '0')}`
        },
    }
}

export default useIdleTimeout
